import './restore-policy.scss';
import React, { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import GenericModal from '../../shared/generic-modal/generic-modal';
import CustomIcon, { IconType } from '../../shared/custom-icon/custom-icon';
import Alert from '../../shared/alert/alert';
import { useStores } from '../../store';
import { useNavigate } from 'src/utils/use-navigate';
import usePromiseFeedback from '../../shared/promise-feedback/usePromiseFeedback';
import { toast } from '../../shared/toast-notifications';
import {DisableReasons, NotificationType} from '../../../utils/enums';
import { PolicyActionDto, PolicyActionModal } from '../version-button/policy-action-modal';
import { FwVersionInfo } from '../../../dtos/version.dto';
import { EventType } from '../../../dtos/analytics.dto';
import { MAINTENANCE_ALERT } from '../../../utils/constants';

interface RestorePolicyProps {
    open?: boolean;
    selectedVersionData?: FwVersionInfo;
    close: () => void;
    closePanel?: () => void;
}

enum RestoreAction {
    RestoreAndApply = 'Restore and Apply',
    RestoreAsDraft = 'Restore as Draft'
}

export const RestorePolicy: React.FC<RestorePolicyProps> = observer(({ open, close, selectedVersionData, closePanel }) => {
    const { fwVersionStore, customerStore } = useStores();
    const navigate = useNavigate();
    const [promiseFeedbackFactory, promiseContextHolder] = usePromiseFeedback();
    const [isApplyPolicyVisible, setIsApplyPolicyVisible] = useState(false);
    const [isRestoreModalVisible, setIsRestoreModalVisible] = useState(true);
    const [actionInProgress, setActionInProgress] = useState<RestoreAction | undefined>();

    const showDraftWarning = useMemo(() => !!fwVersionStore.draftVersion, [fwVersionStore.draftVersion]);
    const versionData = useMemo(() => selectedVersionData ?? fwVersionStore.selectedFwVersion, [fwVersionStore.selectedFwVersion, selectedVersionData]);

    useEffect(() => {
        if (open) {
            setIsApplyPolicyVisible(false);
            setIsRestoreModalVisible(true);
            setActionInProgress(undefined);
        }
    }, [open]);

    const goToLatestVersion = () => {
        const versionId = fwVersionStore.latestVersion?.externalId;
        if (versionId) {
            navigate(`/${customerStore.customerId}/${customerStore.serviceId}/protection/version/${versionId}/${fwVersionStore.versionProtectionPath}`,
                { replace: true });
        }
    };

    const removeDraftIfNeeded = (cb: () => void) => {
        const { draftVersion } = fwVersionStore;
        if (draftVersion) {
            promiseFeedbackFactory({
                promise: fwVersionStore.deleteVersion(draftVersion.externalId),
                successCallback: () => {
                    cb();
                },
                notifObj: { show: false }
            });
        }
        else {
            cb();
        }
    };

    const onVersionRestore = async (versionId: string, versionSid: number) => {
        setActionInProgress(RestoreAction.RestoreAsDraft);
        removeDraftIfNeeded(() => {
            createDraft(versionId, versionSid);
        });
    };

    const createDraft = async (versionId: string, versionSid: number) => {
        promiseFeedbackFactory({
            promise: fwVersionStore.createDraftVersion(versionId, versionSid, true),
            successCallback: () => {
                setActionInProgress(undefined);
                toast(NotificationType.SUCCESS,
                    `Policy was restored as draft v${fwVersionStore.draftVersion?.versionNumber}`);
                goToLatestVersion();
                close();
                closePanel?.();
            },
            notifObj: { show: false }
        });
    };

    const applyPolicy = (data: PolicyActionDto) => {
        const { policyName, policyComments } = data;
        const versionId = versionData?.externalId || '';
        fwVersionStore.sendGeneralEvent(EventType.LinkClicked, { button: 'restoreAndApply' });
        removeDraftIfNeeded(async () => {
            closePanel?.();
            close();
            promiseFeedbackFactory({
                promise: fwVersionStore.restorePolicyAndApply(
                    versionId,
                    policyName,
                    policyComments),
                successCallback: async () => {
                    await fwVersionStore.loadVersionsList();
                    setIsApplyPolicyVisible(false);
                    goToLatestVersion();
                },
                notifObj: { show: false }
            });
        });
    };

    const restorePolicyModalBody = useMemo(() => (<>
        <div className={'restore-policy-description'}>You are about to restore policy:</div>
        <div className={'restore-policy-details'}>
            <div><CustomIcon type={IconType.IP_CONTROL} />{customerStore.selectedLocation?.serviceName || customerStore.selectedLocation?.adiCircuitId}</div>
            <div><CustomIcon type={IconType.EMPTY_SHIELD} />{`V${versionData?.versionNumber ?? ''}`}</div>
        </div>
        {showDraftWarning && <Alert message={'Note. Your current draft changes will be lost.'} isLight={true}></Alert>}
    </>), [showDraftWarning, customerStore.selectedLocation, versionData]);

    const actionButtons = [
        {
            text: RestoreAction.RestoreAsDraft,
            onClick: () => {
                fwVersionStore.sendGeneralEvent(EventType.LinkClicked, { button: 'restoreAsDraft' });
                onVersionRestore(versionData?.externalId || '', customerStore.serviceId);
            },
            className: `secondary-btn ${actionInProgress === RestoreAction.RestoreAsDraft ? 'loading' : ''}`,
            disabled: fwVersionStore.isActiveVersionDeactivate,
            disableReason: fwVersionStore.isActiveVersionDeactivate ? DisableReasons.CREATE_DRAFT_NOT_AVAILABLE_DEACTIVATED : ''
        },
        {
            text: RestoreAction.RestoreAndApply,
            onClick: () => {
                setIsApplyPolicyVisible(true);
                setIsRestoreModalVisible(false);
            },
            disabled: !customerStore.isAvailable,
            disableReason: !customerStore.isAvailable ? MAINTENANCE_ALERT : '',
            className: `primary-btn`
        }];

    const onCloseApplyPolicy = () => {
        setIsApplyPolicyVisible(false);
        close();
    };

    return open ? <>
        <PolicyActionModal visible={isApplyPolicyVisible} close={onCloseApplyPolicy} ok={applyPolicy} />
        <GenericModal open={isRestoreModalVisible}
            body={restorePolicyModalBody}
            onCancel={close}
            title={'Restore Policy'}
            actionButtons={actionButtons}
            wrapClassName={`restore-policy-modal ${actionInProgress === RestoreAction.RestoreAsDraft ? 'disabled' : ''}`} />
        {promiseContextHolder}
    </> : null;
});

